/**
 * UTM Attribution Utilities
 *
 * Captures and stores UTM parameters from the launch URL so they can be
 * attached to analytics events (e.g. captcha_verified) later in the app lifecycle.
 *
 * Design decisions:
 * - Non-blocking: UTM capture is fire-and-forget, never delays app startup
 * - AsyncStorage keyed to session (not user) — wiped on next cold start
 * - All fields optional — missing params are omitted from event payloads
 *
 * @module lib/utm
 * @see NocoDB #795
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

/** AsyncStorage key for cached UTM params */
const UTM_STORAGE_KEY = 'utm_attribution_v1';

/** UTM parameter names as per IAB standard */
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

/**
 * Parse UTM params from a URL string.
 * Supports both query string (?utm_source=...) and fragment (#utm_source=...) formats.
 */
export function parseUtmParams(url: string): UtmParams {
  const result: UtmParams = {};

  try {
    // Try both query string and hash fragments
    const [base, hash] = url.split('#');
    const queryString = base.includes('?') ? base.split('?')[1] : '';
    const hashString = hash?.includes('?') ? hash.split('?')[1] : (hash || '');

    // Parse query string first, then hash (query takes precedence)
    const searchParams = new URLSearchParams(hashString + '&' + queryString);

    for (const param of UTM_PARAMS) {
      const value = searchParams.get(param);
      if (value) {
        result[param] = value;
      }
    }
  } catch {
    // Silently ignore malformed URLs
  }

  return result;
}

/**
 * Persist UTM params from the launch URL to AsyncStorage.
 * Called once at app startup from _layout.tsx (non-blocking).
 */
export async function captureAndStoreUtmParams(launchUrl: string | null): Promise<void> {
  try {
    if (!launchUrl) return;

    const params = parseUtmParams(launchUrl);

    // Only write if we actually found UTM params
    if (Object.keys(params).length > 0) {
      await AsyncStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    }
  } catch {
    // Non-blocking - UTM loss is acceptable vs. crashing app startup
  }
}

/**
 * Read previously stored UTM params from AsyncStorage.
 * Returns an empty object if none were captured.
 * Used by analytics call sites (e.g. captcha_verified).
 */
export async function getStoredUtmParams(): Promise<UtmParams> {
  try {
    const raw = await AsyncStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UtmParams;
  } catch {
    return {};
  }
}
